import { plural } from '@/lib/plural';
import { stageKindLabel } from '@/lib/stage-kind';

import type { DeadlineItem, PendingItem, Person } from './types';
import { businessToday } from '@/lib/time';

// Presentación pura del Panel del QMS (so=430, spec §3). Las fechas relativas salen
// del `daysLeft` del servidor, ya calculado en el día de negocio (spec D10).

const ONE_DECIMAL = new Intl.NumberFormat('es-CR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  useGrouping: false,
});

export type CoverageTone = 'accent' | 'warn' | 'danger';

export interface BadgeSpec {
  label: string;
  cls: 'b-red' | 'b-warn' | 'b-info' | 'b-mut';
}

export interface ResponsibleView {
  person: Person | null;
  /** Personas además de la primera ("+N"). */
  extra: number;
  note: string | null;
}

/** "hoy", "en 2 días", "hace 6 días". */
export function relativeDue(daysLeft: number): string {
  if (daysLeft === 0) {
    return 'hoy';
  }
  const span = plural(Math.abs(daysLeft), 'día', 'días');
  return daysLeft > 0 ? `en ${span}` : `hace ${span}`;
}

/** "86,8" (sin el signo, que la vista compone); "—" si no hay denominador. */
export function formatPctEs(pct: number | null): string {
  return pct === null ? '—' : ONE_DECIMAL.format(pct);
}

/** Umbrales de cobertura por norma (spec D6): ≥ 90 acento, ≥ 70 advertencia. */
export function coverageTone(pct: number): CoverageTone {
  if (pct >= 90) {
    return 'accent';
  }
  return pct >= 70 ? 'warn' : 'danger';
}

export function deadlineBadge(item: DeadlineItem): BadgeSpec {
  if (item.overdue) {
    return {
      label: item.escalatedTo.length > 0 ? 'Vencido · escalado' : 'Vencido',
      cls: 'b-red',
    };
  }
  if (item.stage === null) {
    return { label: 'Revisión periódica', cls: 'b-info' };
  }
  return { label: stageKindLabel(item.stage), cls: 'b-warn' };
}

export function pendingBadge(item: PendingItem): BadgeSpec {
  if (item.overdue) {
    return { label: 'vencida', cls: 'b-red' };
  }
  if (item.daysLeft === null) {
    return { label: 'sin plazo', cls: 'b-mut' };
  }
  return item.daysLeft === 0
    ? { label: 'vence hoy', cls: 'b-warn' }
    : { label: relativeDue(item.daysLeft), cls: 'b-info' };
}

export function responsibleLabel(item: DeadlineItem): ResponsibleView {
  const [first, ...rest] = item.responsibles;
  if (first === undefined) {
    return {
      person: null,
      extra: 0,
      note: item.assignedByPosition ? 'Asignado por puesto' : 'Sin responsable',
    };
  }
  return {
    person: first,
    extra: rest.length,
    note: item.assignedByPosition ? 'y por puesto' : null,
  };
}

/** Dato de contexto de una lista con tope: nunca oculta el total (so=410 D8). */
export function countLabel(shown: number, total: number, suffix: string): string {
  return total > shown ? `${shown} de ${total} · ${suffix}` : `${total} · ${suffix}`;
}

/** Columna "Vence": una etapa que ya venció hoy no se anuncia como "hoy". */
export function dueText(item: DeadlineItem): string {
  return item.overdue && item.daysLeft === 0 ? 'venció hoy' : relativeDue(item.daysLeft);
}

/** Mes en curso del día de negocio: del día 1 a hoy (spec D7). */
export function monthRange(now: Date): { desde: string; hasta: string } {
  const hasta = businessToday(now);
  return { desde: `${hasta.slice(0, 8)}01`, hasta };
}
